import React from "react";
import { Button } from "antd";
import { FaFirstAid } from "react-icons/fa";
import { useSelector } from "react-redux";
import { getColorStylesFromHex, getDateString } from "@utils/calendar_utils";
import { RestTravelBadge } from "./RestTravelBadge";

/**
 * DateCellContent's counterpart for the slim /calendar/month cell contract: the
 * server sends a resolved base hex plus fill percent, and boolean facts
 * (is_game / has_training / has_injuries) in place of the full per-date payload.
 * Renders identically to v1 — see getColorStylesFromHex for the tint derivation.
 */
export const DateCellContentV2 = ({
  date,
  info,
  onClick = () => {},
  showGameAndTraining,
  fontSize = 11,
  popOutView = false
}) => {
  // One selector per field rather than the whole team slice: a month grid mounts 42
  // of these per calendar, and the slice gets a new reference on all 55 of its
  // actions, so subscribing to it re-rendered every cell on team data it never
  // reads. Never combine these into one object-returning selector — a fresh object
  // each call defeats the reference check and is worse than the whole slice.
  const difficultyBarConfig = useSelector(({ team }) => team.info.difficulty_bar_config);
  const calendarDayTags = useSelector(({ team }) => team.calendarDayTags);
  const calendarTags = useSelector(({ team }) => team.calendarTags);

  const dateString = getDateString(date);

  if (!info) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <Button className="no-training-day-btn">
          <RestTravelBadge dateString={dateString} size={16} />
        </Button>
      </div>
    );
  }

  const { borderColor, fontColor, backgroundColor, background } =
    getColorStylesFromHex(info, difficultyBarConfig);

  const hasBadge = (calendarDayTags?.[dateString] ?? []).some((e) => calendarTags?.[e.tag_id]);

  const gameTraining = info.is_game ? "G" : "T";
  const score = info.score ? `${Math.round(info.score)}%` : gameTraining;

  return (
    <div
      onClick={() => onClick(date)}
      style={{ cursor: "pointer", height: "100%" }}
    >
      <Button
        style={{
          color: fontColor,
          background: background || backgroundColor,
          border: `${info.is_game ? 2 : 1}px solid ${borderColor}`,
          width: "100%",
          height: "80%",
          display: "flex",
          flexDirection: popOutView ? "row" : "column",
          columnGap: 8,
          justifyContent: "center",
          alignItems: "center",
          fontSize,
          fontWeight: "bold",
        }}
      >
        <div style={hasBadge && !popOutView ? { marginTop: -6 } : undefined}>{!showGameAndTraining ? gameTraining : score}</div>
        {info.is_prediction && !info.has_training && (
          <div style={{ position: "absolute", top: 0, right: 4 }}>P</div>
        )}
        {info.has_injuries && (
          <FaFirstAid className="injury-icon" size={17} />
        )}
        <RestTravelBadge dateString={dateString} popOutView={popOutView} size={16} />
      </Button>
    </div>
  );
};
